import { observable, action, reaction, makeObservable } from 'mobx';
import { Disposer, InstanceUtils } from '../misc';
import { CustomLinkedSource, CustomStaticSource, CustomSource } from '../types';
import { StaticSourceModel, LinkedSourceModel, SourceModelBase } from '../models';
import { SessionService } from '../../common/services';
import { WidgetValuesProvider } from '../../common/services/WidgetValuesProvider';
import { IAppStoreBase, ISessionStoreWithSources } from '../types';

export default class SourceStore extends Disposer {
    sources: SourceModelBase[] = [];

    constructor(
        private readonly appStore: IAppStoreBase,
        private readonly sessionStore: ISessionStoreWithSources,
        private readonly sessionService: SessionService
    ) {
        super();

        makeObservable(this, {
            sources: observable,
            createOrUpdateSources: action
        });

        this.reactions.push(
            reaction(
                () => this.sessionStore.response,
                () => {
                    if (!this.sessionStore.inProgress) {
                        this.createOrUpdateSources();
                    }
                }
            )
        );
    }

    createOrUpdateSources() {
        if (!this.sessionStore.response) {
            return;
        }

        this.sources = this.sessionStore.response.sources.map(source => {
            const existingSource = this.sources.find(s => s.id === source.id);
            return existingSource ? this.updateSource(source, existingSource) : this.createSource(source);
        });
    }

    dispose() {
        this.disposeReactions();
    }

    private createSource(source: CustomSource) {
        if (!source.link) {
            return new StaticSourceModel(source as CustomStaticSource);
        }

        const widgetValuesProvider = new WidgetValuesProvider(
            this.sessionService,
            source.link,
            this.appStore.sessionId,
            this.appStore.applicationData
        );

        return new LinkedSourceModel(source as CustomLinkedSource, widgetValuesProvider);
    }

    private updateSource(source: CustomSource, existingSource: SourceModelBase) {
        if (InstanceUtils.isStaticSource(existingSource)) {
            existingSource.update(source as CustomStaticSource);
        }

        return existingSource;
    }
}
